import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Upload, FileText, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { processService } from "../../../services/process.service";

export function StatusImportDialog({ isOpen, onOpenChange, onSuccess, processoId }) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv"))) {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error("Por favor, selecione um arquivo CSV válido.");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Selecione um arquivo primeiro.");
      return;
    }

    setIsUploading(true);
    setResult(null);

    try {
      const data = await processService.bulkStatusUpdate(file, processoId);
      setResult(data);
      toast.success("Processamento concluído!");
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.error(error.message || "Erro ao processar arquivo");
    } finally {
      setIsUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(reset, 300);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Atualizar Status</DialogTitle>
          <DialogDescription>
            Faça upload de um CSV com as colunas <strong>cpf</strong> e <strong>status</strong> (pago, expirado, pendente, assinado).
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 flex flex-col items-center gap-4">
          {!result ? (
            <div 
              className={`w-full border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-3 transition-colors cursor-pointer
                ${file ? 'border-cessaoYellow bg-cessaoYellow/5' : 'border-slate-200 hover:border-slate-300 bg-slate-50'}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv"
                className="hidden"
              />
              
              {file ? (
                <>
                  <FileText className="w-10 h-10 text-cessaoYellow" />
                  <div className="text-center">
                    <p className="font-medium text-slate-900">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-slate-400" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-700">Clique para selecionar o CSV</p>
                    <p className="text-xs text-slate-500 mt-1">Delimitador padrão: ponto e vírgula (;)</p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="w-full space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-slate-900">Resultado do Processamento</h4>
                </div>
                <p className="text-sm text-slate-700">
                  Registros atualizados: <span className="font-bold">{result.updated_rows}</span>
                </p>
                {result.errors?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-red-600 mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Erros encontrados ({result.errors.length}):
                    </p>
                    <div className="max-h-32 overflow-y-auto border rounded bg-white p-2">
                       {result.errors.map((err, i) => (
                         <p key={i} className="text-[10px] text-slate-600 border-b last:border-0 py-1">{err}</p>
                       ))}
                    </div>
                  </div>
                )}
              </div>
              <Button variant="ghost" className="w-full" onClick={reset}>
                Processar outro arquivo
              </Button>
            </div>
          )}

          <div className="w-full bg-blue-50 border border-blue-100 rounded-lg p-3 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 shrink-0" />
            <div className="text-[11px] text-blue-700 leading-relaxed">
              <strong>Importante:</strong> Esta ação atualizará o status dos vínculos ativos dos substituídos <strong>neste processo</strong>. Certifique-se de que os CPFs e Status no arquivo estão corretos.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Fechar</Button>
          {!result && (
            <Button 
              className="bg-cessaoYellow hover:bg-cessaoYellow/90 text-white"
              onClick={handleUpload}
              disabled={!file || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                "Confirmar Importação"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
